import Link from "next/link";
import Tealium from "@4tn/webx-analytics/lib/Tealium";
import { styled } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import TextSkeleton from "@pageContent/common/Skeletons/TextSkeleton";
import { CONTENT_LANGUAGE } from "@constants/consts";
import { colors, fonts } from "@constants/cssVariables";
import { TEALIUM_EVENT_CATEGORY, TEALIUM_EVENT_NAME } from "@constants/tealium.constants";
import getPageCategory, { getRouteSlug } from "@utils/common/getPageCategory";
import { useNoticeError } from "@utils/common/newRelic";
import { ReturnFetchData, fetchByEndpoint } from "@utils/pageContent/fetchData";

const ArticlePanelItem = styled("div", { shouldForwardProp: (prop) => prop !== "hasBorder" })<{ hasBorder: boolean }>(
  ({ theme, hasBorder }) => ({
    display: "flex",
    font: fonts.header7,
    color: colors.neutral80,
    marginBottom: theme.spacing(4),
    borderBottom: hasBorder ? `1px solid ${colors.neutral10}` : "none",
  })
);

const ArticlePanelNumber = styled("div")(({ theme }) => ({
  font: fonts.header4,
  color: colors.neutral40,
  minWidth: 24,
  marginRight: theme.spacing(3),
  textAlign: "center",
}));

const ArticlePanelTitle = styled("div")(({ theme }) => ({
  font: fonts.header4,
  color: colors.neutral90,
  marginBottom: theme.spacing(3),
}));

const ArticleTitle = styled("div")(({ theme }) => ({
  overflow: "hidden",
  display: "-webkit-box",
  textOverflow: "ellipsis",
  wordBreak: "break-word",
  marginBottom: theme.spacing(4),
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: "3",

  "&:hover": {
    textDecoration: "underline",
  },
}));

const SkeletonsContainer = styled("div", { shouldForwardProp: (prop) => prop !== "hasNumbers" })<{
  hasNumbers?: boolean;
}>(({ theme, hasNumbers }) => ({
  paddingBottom: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderBottom: `1px solid ${colors.neutral10}`,
  display: hasNumbers ? "flex" : "block",
  gap: theme.spacing(4),

  "&:nth-of-type(5n)": {
    borderBottom: "none",
  },
}));

const ArticleTitleSkeletonContainer = styled("div")(({}) => ({
  width: "100%",
}));

const fetchCollection = async (url: string) => {
  const initialData = await fetchByEndpoint<LinkList | CategoryBasedCollection>(url);

  if (!initialData) return null;

  if (initialData.contentType === "categoryBasedCollection") {
    const justInCollection = await fetchByEndpoint<ReturnFetchData<PageLink>>(`${initialData.endpoint}?limit=5`);
    return { ...justInCollection, title: initialData.title, type: initialData.type };
  }

  return { ...initialData, items: initialData.items.slice(0, 5) };
};

export const PanelSkeletonNumeric: React.FC = () => {
  return (
    <>
      <TextSkeleton width="70%" height={24} sx={{ marginBottom: 5 }} />
      {[...Array(5)].map((_, i) => (
        <SkeletonsContainer key={`panel-skeleton-numeric-${i}`} hasNumbers>
          <TextSkeleton height={20} width={15} />
          <ArticleTitleSkeletonContainer>
            <TextSkeleton height={12} sx={{ marginBottom: 2 }} />
            <TextSkeleton height={12} sx={{ marginBottom: 2 }} />
            <TextSkeleton height={12} sx={{ marginBottom: 2 }} width={"85%"} />
          </ArticleTitleSkeletonContainer>
        </SkeletonsContainer>
      ))}
    </>
  );
};

export const PanelSkeleton: React.FC = () => {
  return (
    <>
      <TextSkeleton width="70%" height={24} sx={{ marginBottom: 5 }} />
      {[...Array(5)].map((_, i) => (
        <SkeletonsContainer key={`panel-skeleton-${i}`}>
          <TextSkeleton height={12} sx={{ marginBottom: 2 }} />
          <TextSkeleton height={12} sx={{ marginBottom: 2 }} />
          <TextSkeleton height={12} sx={{ marginBottom: 2 }} width={"85%"} />
        </SkeletonsContainer>
      ))}
    </>
  );
};

const trackClick = (collectionTitle: string, collectionItem: PageLink, position: number) => {
  const pageCategory = getPageCategory(collectionItem.url);

  Tealium.link({
    event_category: TEALIUM_EVENT_CATEGORY.CONTENT_LISTING,
    event_label: collectionTitle,
    event_value: position,
    event_name: TEALIUM_EVENT_NAME.CONTENT_LISTING_CLICK,
    content_category: collectionItem.category?.title || null,
    content_datecreated: collectionItem.createdAt || null,
    content_datepublished: collectionItem.createdAt || null,
    content_id: getRouteSlug(collectionItem.url, pageCategory),
    content_language: CONTENT_LANGUAGE,
    content_datemodified: collectionItem.updatedAt || null,
    content_name: collectionItem.title,
    content_type: pageCategory,
  });
};

const ArticlePanel: React.FC<{ url: string; isNumeric?: boolean }> = ({ url, isNumeric }) => {
  const {
    data: collection,
    isLoading,
    error,
  } = useQuery({
    queryKey: [url],
    queryFn: () => fetchCollection(url),
  });

  useNoticeError(error, { queryKey: url });

  if (isLoading) return isNumeric ? <PanelSkeletonNumeric /> : <PanelSkeleton />;

  if (!collection) return null;

  return (
    <>
      <ArticlePanelTitle>{collection?.title}</ArticlePanelTitle>
      {collection?.items.map((collectionItem, index) => (
        <ArticlePanelItem
          key={collectionItem.id}
          hasBorder={index < collection.items.length - 1}
          onClick={() => trackClick(collection.title, collectionItem, index + 1)}
        >
          {collection.type === "numeric-collection" && <ArticlePanelNumber>{index + 1}</ArticlePanelNumber>}
          {collectionItem.url && (
            <Link href={collectionItem.url} target={collectionItem.target}>
              <ArticleTitle>{collectionItem.title}</ArticleTitle>
            </Link>
          )}
        </ArticlePanelItem>
      ))}
    </>
  );
};

export default ArticlePanel;
